import TitleBar from '../../../components/TitleBar';
import { Footer } from '../../../components/common/Footer';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../../components';
import { useQuery } from '@apollo/client';
import { FETCH_MITIGATION_DIVISIONS } from '../queries';
import TableV3 from '../../../components/generic/Table/TableV3';
import { tableSortingMethods } from '../../../utils';
import { ColumnsType } from 'antd/es/table';
import { useUserContext } from '../../../context/PermissionsContext';
import QueryErrorBoundary from '../../../components/error-handling';
import { getFilteredTags } from '../../../utils/getFilteredTags';
import { baseColumns } from './exportColumns';
import { FETCH_DIVISIONS_BY_LOCATIONS } from '../queries/Mitigation';
import TitleUpdater from '../../../components/common/TitleUpdater';

type DivisionFinancial = {
  __typename: 'DivisionFinancial';
  name: string;
  uuid: string;
  isDefault: boolean;
  locationsPerDivision: number;
  divisionMitigationAverage: number;
};

type FindAllDivisionsResponse = {
  findAllDivisions: DivisionFinancial[];
};

function InternalExternalMitigation() {
  const navigate = useNavigate();

  const { data, loading, error } = useQuery<FindAllDivisionsResponse>(
    FETCH_MITIGATION_DIVISIONS,
  );

  const { selectedTags, isDivision, isLocation } = useUserContext();

  const { data: divisionsByLocations } = useQuery(
    FETCH_DIVISIONS_BY_LOCATIONS,
    {
      variables: {
        locations: { locations: selectedTags },
      },
      skip: !isLocation,
    },
  );

  const divisionsByLocationsData =
    divisionsByLocations?.findDivisionsByLocation || [];

  const divisionsData = data?.findAllDivisions || [];

  const filteredDivisions = getFilteredTags({
    data: divisionsData,
    selectedTags,
    condition: isDivision,
    lookUpKey: 'uuid',
  });

  const columns: ColumnsType<DivisionFinancial> = [
    {
      title: 'Division',
      dataIndex: 'name',
      key: 'name',
      align: 'left',
      width: 200,
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: (text: string) => (
        <span className="custom-table--column">{text}</span>
      ),
    },
    {
      title: 'Location Count',
      dataIndex: 'locationsPerDivision',
      key: 'locationsPerDivision',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.locationsPerDivision,
          b.locationsPerDivision,
        ),
      render: (text: number) => (
        <span className="custom-table--column">{text ?? 0}</span>
      ),
    },
    {
      title: 'Division Mitigation Average',
      dataIndex: 'divisionMitigationAverage',
      key: 'divisionMitigationAverage',
      align: 'right',
      width: 100,
      sorter: (a, b) =>
        tableSortingMethods.number(
          a.divisionMitigationAverage,
          b.divisionMitigationAverage,
        ),
      render: (text: number) => (
        <span className="custom-table--column">
          {(text?.toFixed(0) || 0) + '%'}
        </span>
      ),
    },
  ];

  const extendActions = (record: DivisionFinancial) => {
    return (
      <Button
        type="primary-rounded"
        className="text-sm font-semibold"
        onClick={() =>
          navigate(`/${record.uuid}/mitigation?division=${record.name}`)
        }
      >
        Quantify Mitigation
      </Button>
    );
  };

  return (
    <>
      <TitleUpdater title={`Location Mitigation`} />
      <TitleBar
        title="Location Mitigation (Internal/External)"
        subtitle="Division Selection"
        brief="Select a division to start adding internal and external mitigation options."
      />
      <QueryErrorBoundary error={error}>
        <TableV3
          title="Divisions"
          columns={columns}
          data={isLocation ? divisionsByLocationsData : filteredDivisions}
          loading={loading}
          extendActions={extendActions}
          exportExcelColumns={baseColumns}
        />
      </QueryErrorBoundary>
      <Footer backPath={'/exposures'} />
    </>
  );
}

export default InternalExternalMitigation;
